import { useFormikContext } from 'formik';
import { debounce } from 'lodash';
import { shallowEqual } from 'react-redux';
import { useEffect, useMemo, useRef } from 'react';

export interface FormDebouncedAutoSubmitProps<
  TFormValue extends Record<string, unknown> = Record<string, unknown>,
> {
  /**
   * Should autosubmit be skipped while the form is pristine (no changes made)?
   * @default true
   */
  skipPristine?: boolean;

  /**
   * Delay before submitting after a value change.
   * @default 800
   */
  debounceMs?: number;

  /**
   * Compares previous and current form values.
   * Return true to skip submitting.
   * @default shallowEqual
   */
  compare?: (prevValues: TFormValue, currentValues: TFormValue) => boolean;
}

const FormDebouncedAutoSubmitComponent = <
  TFormValue extends Record<string, unknown> = Record<string, unknown>,
>(
  props: FormDebouncedAutoSubmitProps<TFormValue>,
) => {
  const { skipPristine, debounceMs, compare } = Object.assign(
    {},
    FormDebouncedAutoSubmit.DEFAULT_PROPS,
    props,
  );

  const { errors, dirty, values, submitForm, isSubmitting } = useFormikContext<TFormValue>();

  const isFirstRender = useRef(true);
  const prevValuesRef = useRef<TFormValue>(values);
  const latestValuesRef = useRef<TFormValue>(values);
  const pendingSubmitRef = useRef(false);
  const submittedValuesRef = useRef<TFormValue | null>(null);
  const wasSubmittingRef = useRef(false);

  latestValuesRef.current = values;

  const shouldSkipPristine = useMemo(() => skipPristine && !dirty, [skipPristine, dirty]);
  const skipErrored = useMemo(() => Object.keys(errors).length > 0, [errors]);

  const debouncedSubmit = useMemo(
    () =>
      debounce(() => {
        submittedValuesRef.current = latestValuesRef.current;
        pendingSubmitRef.current = true;
        void submitForm();
      }, debounceMs),
    [submitForm, debounceMs],
  );

  useEffect(() => {
    return () => debouncedSubmit.cancel();
  }, [debouncedSubmit]);

  useEffect(() => {
    const prevValues = prevValuesRef.current;
    const changed = !compare!(prevValues, values);

    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevValuesRef.current = values;
      return;
    }

    if (!changed) {
      return;
    }

    pendingSubmitRef.current = true;

    if (skipErrored || shouldSkipPristine) {
      debouncedSubmit.cancel();
      return;
    }

    if (isSubmitting) {
      debouncedSubmit.cancel();
      return;
    }

    debouncedSubmit();
  }, [values, compare, skipErrored, shouldSkipPristine, isSubmitting, debouncedSubmit]);

  useEffect(() => {
    const submitJustFinished = wasSubmittingRef.current && !isSubmitting;
    wasSubmittingRef.current = isSubmitting;

    if (!submitJustFinished) {
      return;
    }

    if (submittedValuesRef.current != null) {
      prevValuesRef.current = submittedValuesRef.current;
      submittedValuesRef.current = null;
    }

    if (skipErrored || shouldSkipPristine) {
      pendingSubmitRef.current = false;
      debouncedSubmit.cancel();
      return;
    }

    const hasNewChanges = !compare!(prevValuesRef.current, latestValuesRef.current);
    pendingSubmitRef.current = hasNewChanges;

    if (!hasNewChanges) {
      return;
    }

    debouncedSubmit();
  }, [isSubmitting, compare, skipErrored, shouldSkipPristine, debouncedSubmit]);

  return null;
};

FormDebouncedAutoSubmitComponent.displayName = 'Form.DebouncedAutoSubmit';

export const FormDebouncedAutoSubmit = Object.assign(FormDebouncedAutoSubmitComponent, {
  DEFAULT_PROPS: {
    skipPristine: true,
    debounceMs: 800,
    compare: shallowEqual,
  } as Partial<FormDebouncedAutoSubmitProps<Record<string, unknown>>>,
});
